import { useQuery } from "@tanstack/react-query"

import {
  getHistory,
  getPersonBriefBulk,
  getPreMeetingAccountDetails,
  getUpcomingMeetingBriefs
} from "~/api/pre-meeting"
import { getPersonDetails } from "~/api/priorities"
import useGlobalStore from "~/store/global"
import { ITokenInfo } from "~/types/global"
import { getPmbSection, type PmbSectionCtx } from "~/api/pre-meeting-v2"
import { PmbSectionResult } from "~/types/pre-meeting"

export const usePmbSection = <TPayload = Record<string, unknown>>(
  sectionId: string,
  ctx: PmbSectionCtx,
  enabled: boolean
) => {
  const tokenInfo = useGlobalStore((store) => store.tokenInfo)
  return useQuery<PmbSectionResult<TPayload> | null>({
    queryKey: [
      "pmb-section",
      sectionId,
      JSON.stringify({
        accountId: ctx.accountId ?? null,
        linkedinUrl: ctx.linkedinUrl ?? null,
        personTitle: ctx.personTitle ?? null,
        opportunityStage: ctx.opportunityStage ?? null
      })
    ],
    queryFn: async () => {
      const res = await getPmbSection(tokenInfo as ITokenInfo, sectionId, ctx)
      return (res?.data?.data ?? null) as PmbSectionResult<TPayload> | null
    },
    enabled: enabled && !!tokenInfo,
    staleTime: 5 * 60 * 1000
  })
}

export const useGetPreMeetingBrief = (
  { website, company_id, session_id, name, hookInputData }: any,
  enabled: boolean,
  retryKey: any
) => {
  const tokenInfo = useGlobalStore((store) => store.tokenInfo)
  return useQuery({
    queryKey: [
      "pre-meeting",
      JSON.stringify({
        website: website,
        company_id: company_id,
        name,
        hookInputData
      }),
      session_id,
      retryKey
    ],
    queryFn: async () => {
      const res = await getPreMeetingAccountDetails(
        tokenInfo as ITokenInfo,
        website,
        company_id,
        session_id,
        name,
        hookInputData
      )
      return res.data
    },
    enabled: enabled && !!tokenInfo
  })
}

export const useGetPreviousPreMeetingBrief = (enabled: boolean) => {
  const tokenInfo = useGlobalStore((store) => store.tokenInfo)
  return useQuery({
    queryKey: ["previous-pre-meeting-brief"],
    queryFn: () => getHistory(tokenInfo!),
    enabled: enabled && !!tokenInfo,
    staleTime: 5 * 60 * 1000
  })
}

export const useGetUpcomingMeetingBriefs = (enabled: boolean) => {
  const tokenInfo = useGlobalStore((store) => store.tokenInfo)
  return useQuery({
    queryKey: ["upcoming-meeting-briefs"],
    queryFn: () => getUpcomingMeetingBriefs(tokenInfo!),
    enabled: enabled && !!tokenInfo,
    staleTime: 5 * 60 * 1000
  })
}

export const useGetPersona = (
  { linkedin_url }: { linkedin_url: string },
  enabled: boolean
) => {
  const tokenInfo = useGlobalStore((store) => store.tokenInfo)
  return useQuery({
    queryKey: [
      "pre-meeting-get-persona",
      JSON.stringify({
        user_id: tokenInfo?.user_id
      }),
      linkedin_url
    ],
    queryFn: async () => {
      const res = await getPersonDetails(
        tokenInfo as ITokenInfo,
        linkedin_url,
        "pre_meeting_brief"
      )
      return res.data.data.persona || null
    },
    enabled: enabled && !!tokenInfo
  })
}

export const useGetPersonData = (
  {
    users,
    session_id,
    timestamp
  }: {
    users: any
    session_id: string
    timestamp: string
  },
  enabled: boolean,
  retryKey: any
) => {
  const tokenInfo = useGlobalStore((store) => store.tokenInfo)
  return useQuery({
    queryKey: [
      "pre-meeting",
      JSON.stringify({
        users
      }),
      timestamp,
      retryKey
    ],
    queryFn: async () => {
      if (users.length === 0) return []

      const res = await getPersonBriefBulk(
        tokenInfo as ITokenInfo,
        users,
        session_id
      )
      return res.data
    },
    enabled: enabled && !!tokenInfo
  })
}
